/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import classNames from 'classnames';
import { CnRankingColumnsType, CnRankingItemProps, CnRankingBaseProps } from './types';
import { TOP_RANKING_NODE_LIST } from './const';
import './index.scss';

const PREFIX_CLASS_NAME = 'cn-ranking';

const baseClassNames = (name: string, ...args: classNames.ArgumentArray) =>
  classNames(`${PREFIX_CLASS_NAME}-${name}`, ...args);

const RankingItemContent = <TData,>(props: CnRankingItemProps<TData>) => {
  if (props.column?.hide) return null;

  if (props.isHead) {
    if (props.column?.hideTitle) return null;
    return props.column?.title || '';
  }

  if (typeof props.column?.render === 'function') {
    const textNode = props.column.dataIndex ? props.rollData?.[props.column.dataIndex] : undefined;
    const element = props.column.render(textNode, props.rollData, props?.rollIndex || 0);

    return element;
  }

  if (props.column?.dataIndex) {
    return props.rollData?.[props.column?.dataIndex] || null;
  }

  return null;
};

const RankingItem = <TData,>(props: CnRankingItemProps<TData>) => {
  const renderElement: keyof React.ReactHTML = props.isHead ? 'th' : 'td';

  return React.createElement(
    renderElement,
    {
      className: baseClassNames('column', props.className),
      style: {
        textAlign: props.column?.align || 'left',
      },
    },
    <div className={baseClassNames('column-content')}>
      <RankingItemContent {...props} />
    </div>,
  );
};

/**
 * @description 排行榜组件
 */
export const CnRankingBase = <T,>(props: CnRankingBaseProps<T>) => {
  const isValidData =
    Array.isArray(props.data) &&
    props.data.length > 0 &&
    props.rankingDimension &&
    Array.isArray(props.rankingMeasures) &&
    props.rankingMeasures.length > 0;
  if (!isValidData) return null;

  const { showTitle = true, maxCount = 10, showGroup = 'all' } = props;

  const firstMeasureColumn = props.rankingMeasures?.[0];
  const firstDataIndex = firstMeasureColumn?.dataIndex;
  if (!firstDataIndex) return null;

  const dataList = props.data
    .slice(0, maxCount)
    .sort((a, b) => Number(b[firstDataIndex]) - Number(a[firstDataIndex]));
  const maxMeasureValue = Math.max(...dataList.map((item) => Number(item[firstDataIndex] || 0)));

  const columns: Array<CnRankingColumnsType<T>> = [
    {
      title: '排序',
      align: 'center',
      className: baseClassNames('column-order'),
      render: (_, record, index) => {
        if (props.topRankingNodes && props.topRankingNodes[index]) {
          return props.topRankingNodes[index];
        }

        const topRankingPreset = TOP_RANKING_NODE_LIST.find(
          (item) => item.type === props.topRankingStyle,
        );
        if (topRankingPreset && topRankingPreset?.nodes?.[index]) {
          return topRankingPreset.nodes?.[index];
        }

        return <div className={baseClassNames('column-order-default')}>{index + 1}</div>;
      },
    },
    {
      ...props.rankingDimension,
      className: classNames(props.rankingDimension.className, baseClassNames('column-dimension')),
    },
    {
      title: firstMeasureColumn.title || '进度',
      className: baseClassNames('column-progress'),
      dataIndex: firstMeasureColumn.dataIndex,
      hideTitle: showGroup === 'all',
      hide: showGroup === 'number' || showGroup === 'measure',
      render: (text) => {
        const percent = (Number(text) / maxMeasureValue) * 100;
        const { CnProgress } = props;

        return (
          <div className={baseClassNames('progress-cell-main')}>
            {percent >= 0 && CnProgress && (
              <CnProgress
                percent={percent}
                size={props.progressSize || 'medium'}
                textRender={() => null}
              />
            )}
          </div>
        );
      },
    },
    ...props.rankingMeasures.map<CnRankingColumnsType<T>>((item) => ({
      ...item,
      align: 'right',
      hide: showGroup === 'progress' || showGroup === 'measure',
    })),
  ];

  return (
    <div
      className={baseClassNames('wrap', props.className)}
      style={{ ...(props.style || {}), maxHeight: props.maxHeight }}
    >
      <table className={classNames(PREFIX_CLASS_NAME)}>
        {Boolean(showTitle) && (
          <thead className={baseClassNames('head')}>
            <tr className={baseClassNames('tr')}>
              {columns.map((column, columnIndex) => (
                <RankingItem
                  isHead
                  key={`column_${columnIndex}`}
                  className={column.className}
                  column={column}
                />
              ))}
            </tr>
          </thead>
        )}

        <tbody className={baseClassNames('body')}>
          {dataList.map((item, index) => (
            <tr
              className={baseClassNames('tr')}
              key={`row_${index}`}
              onClick={() => props.onRowSelect?.(item, index)}
            >
              {columns.map((column, columnIndex) => (
                <RankingItem
                  key={`column_${columnIndex}`}
                  className={column.className}
                  column={column}
                  rollData={item}
                  rollIndex={index}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
